import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGasPump, FaUsers, FaTruck, FaExchangeAlt,
  FaArrowDown, FaArrowUp, FaClock,
  FaDotCircle, FaExclamationTriangle, FaWrench, FaTimesCircle, FaClipboardCheck, FaTint
} from 'react-icons/fa';
import StatsCard from '../components/StatsCard';
import TxnDetailModal from '../components/TxnDetailModal';
import { useApiCall } from '../hooks/useApiCall';
import '../styles/Dashboard.css';

/* ── Vertical tank cylinder visual ─────────────────────────────── */
function TankVisual({ pct, color }) {
  const clamped = Math.max(0, Math.min(100, pct));
  // Top cap: show fuel color only when tank is essentially full, otherwise show empty metal top
  const topCapColor   = clamped >= 98 ? color : 'var(--bg-card2)';
  const topCapBorder  = clamped >= 98 ? color : 'var(--border-color)';
  // At high fill levels, slightly darken the cap so it reads as a 3D top face
  const topCapStyle   = clamped >= 98
    ? { background: color, borderColor: color, filter: 'brightness(0.82)' }
    : { background: topCapColor, borderColor: topCapBorder };
  return (
    <div className="tank-visual">
      {/* measurement ticks */}
      <div className="tank-ticks">
        {[100, 75, 50, 25, 0].map(v => (
          <div key={v} className="tank-tick">
            <span>{v}</span>
            <div className="tank-tick-line" />
          </div>
        ))}
      </div>
      {/* cylinder */}
      <div className="tank-cylinder">
        <div className="tank-cap top" style={topCapStyle} />
        <div className="tank-body">
          {/* fill */}
          <div
            className="tank-fill"
            style={{ height: clamped + '%', background: `linear-gradient(180deg, ${color}bb 0%, ${color} 100%)` }}
          />
          {/* wave on top of fill */}
          {clamped > 3 && (
            <div className="tank-wave" style={{ bottom: clamped + '%', background: color + '55' }} />
          )}
          {/* shine */}
          <div className="tank-shine" />
          {/* % label */}
          <div
            className="tank-pct-label"
            style={{ color: clamped > 50 ? '#fff' : color, textShadow: clamped > 50 ? '0 1px 4px rgba(0,0,0,0.6)' : 'none' }}
          >
            {clamped}%
          </div>
        </div>
        <div className="tank-cap bottom" style={{ background: color, borderColor: color }} />
      </div>
    </div>
  );
}

/* ── Tanker card ────────────────────────────────────────────────── */
function TankerCard({ t, onClick }) {
  const pct = t.level_pct ?? 0;
  const color = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
  return (
    <div className={`dash-tanker-card${t.in_use ? ' in-use' : ''}`} onClick={onClick} style={{ cursor: 'pointer' }}>
      <TankVisual pct={pct} color={color} />
      <div className="dash-tanker-details">
        <div className="dash-tanker-name">
          <FaTruck className="dash-tanker-icon" /> {t.name}
        </div>
        <div className="dash-tanker-badges">
          <span className="fuel-type-badge"><FaGasPump style={{ fontSize: 9 }} /> {t.fuelType}</span>
          <span className={`tanker-status-badge ${t.in_use ? 'in-use' : 'available'}`}>
            <FaDotCircle /> {t.in_use ? 'In Use' : 'Available'}
          </span>
        </div>
        {/* horizontal bar as secondary indicator */}
        <div className="fuel-bar-track" style={{ marginTop: 8 }}>
          <div className="fuel-bar-fill" style={{ width: pct + '%', background: color }} />
        </div>
        <div className="dash-tanker-amounts">
          <span style={{ color, fontWeight: 700, fontSize: 16 }}>{pct}%</span>
          <span className="level-gal">{t.amount} / {t.amount_cap} {t.amount_type}</span>
        </div>
        {pct < 20 && (
          <div className="dash-tanker-warning">
            <FaExclamationTriangle /> Low - refill required
          </div>
        )}
        {t.bowser_check === 'fail' && (
          <div className="dash-tanker-warning dash-tanker-check-fail">
            <FaTimesCircle /> Bowser check failed
          </div>
        )}
        {t.bowser_check === 'not_checked' && (
          <div className="dash-tanker-warning dash-tanker-check-none">
            <FaClipboardCheck /> No bowser check today
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Dashboard ─────────────────────────────────────────────── */
export default function Dashboard() {
  const apiCall = useApiCall();
  const navigate = useNavigate();
  const [time, setTime] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [tankers, setTankers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);  const [selectedTxn, setSelectedTxn] = useState(null);
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiCall('admin/dashboard', 'POST').then(res => {
      if (res?.success) {
        setStats(res.data.stats);
        setTankers(res.data.tankers || []);
        setRecent(res.data.recent_transactions || []);
      }
      setLoading(false);
    });
  }, []);

  const fmtAmount = (v, type) => (v != null && v !== '') ? `${v} ${type}` : '-';
  const fmtTs = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts * 1000);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return (isToday ? 'Today' : d.toLocaleDateString()) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="page-subtitle">Real-time overview of UAS fuel operations</p>
        </div>
        <div className="header-time">
          <FaClock className="time-icon" />
          <span>{time.toLocaleString()}</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading dashboard…</div>
      ) : (
        <>
          {/* ── Stats row ── */}
          <div className="stats-row">
            <StatsCard icon={<FaGasPump />}     label="Fuel dispensed today"  value={fmtAmount(stats?.fuel_today, 'L')} sub="" />
            <StatsCard icon={<FaExchangeAlt />} label="Transactions today"    value={stats?.txn_today ?? '-'}
              sub={stats ? `${stats.txn_out_today ?? 0} OUT · ${stats.txn_in_today ?? 0} IN` : ''} color="#f5af18" />
            <StatsCard icon={<FaTruck />}        label="Total tankers"         value={stats?.total_tankers ?? '-'}
              sub={stats ? `${stats.available_tankers ?? 0} avail · ${stats.active_tankers ?? 0} in use` : ''} color="#8b5cf6" />
            <StatsCard icon={<FaUsers />}        label="Active fuelers"        value={stats?.active_fuelers ?? '-'} color="#35c068" />
          </div>

          {/* ── Tanker visuals ── */}
          <div className="dash-section-header">
            <h3>Tanker fuel levels</h3>
            <span className="dash-card-badge">Live</span>
          </div>
          <div className="dash-tankers-row">
            {tankers.map(t => <TankerCard key={t.id} t={t} onClick={() => navigate('/fuel-levels', { state: { tanker: t } })} />)}
            {tankers.length === 0 && <div className="empty-state">No tankers found</div>}
          </div>

          {/* ── Recent transactions ── */}
          <div className="dash-section-header" style={{ marginTop: 28 }}>
            <h3>Recent transactions</h3>
          </div>
          <div className="dash-txn-card">
            {recent.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px', textAlign: 'center' }}>No recent transactions</div>
            ) : recent.map(tx => {
              const isOut      = tx.type === 'OUT';
              const isIn       = tx.type === 'IN';
              const isSpillage = tx.type === 'SPILLAGE';
              const typeKey    = isSpillage ? 'spillage' : tx.type?.toLowerCase() || 'out';
              const badgeLabel = isOut ? 'OUT' : isIn ? 'IN' : isSpillage ? 'SPILL' : 'CORR';
              const mainLabel  = isOut
                ? `${tx.aircraft ?? '-'}  ·  ${tx.fueler ?? '-'}`
                : isIn
                  ? `Refill - ${tx.vendor ?? '-'}`
                  : isSpillage
                    ? `Spillage`
                    : `Correction`;
              return (
                <div key={tx.id} className="dash-txn-row" onClick={() => setSelectedTxn(tx)} style={{ cursor: 'pointer' }}>
                  <div className={`dash-txn-badge ${typeKey}`}>
                    {isOut ? <FaArrowUp /> : isIn ? <FaArrowDown /> : isSpillage ? <FaTint /> : <FaWrench />}
                    {badgeLabel}
                  </div>
                  <div className="dash-txn-info">
                    <div className="dash-txn-main">{mainLabel}</div>
                    <div className="dash-txn-sub">
                      <span>{tx.tanker}</span>
                      {tx.gas_type && <><span className="dash-txn-dot" /><span>{tx.gas_type}</span></>}
                      {tx.notes && <><span className="dash-txn-dot" /><span style={{ fontStyle: 'italic', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.notes}</span></>}
                    </div>
                  </div>
                  <div className="dash-txn-right">
                    <span className="dash-txn-amount">{fmtAmount(tx.amount, tx.amount_type)}</span>
                    <span className="dash-txn-time">{fmtTs(tx.timestamp)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {selectedTxn && <TxnDetailModal tx={selectedTxn} onClose={() => setSelectedTxn(null)} />}
    </div>
  );
}
